import React, { useState } from 'react';
import { Alert, Button, Form, Table } from 'react-bootstrap';
import { apiGet } from '../../services/api';

export default function AdminAnalyticsTab({ token, handleAuthError }) {
  const [analyticsRange, setAnalyticsRange] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');

  const generateAnalytics = async () => {
    if (!token) return;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const res = await apiGet(`/api/v1/admin/analytics?range=${analyticsRange}`, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setAnalyticsError('Could not generate report.');
        return;
      }
      const json = await res.json();
      setAnalytics(json.data ?? null);
    } catch {
      setAnalyticsError('Could not generate report.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  return (
    <>
      <div className="rwm-admin__analytics-controls mb-4">
        <Form.Group className="mb-3 rwm-admin__range-group">
          <Form.Label htmlFor="rwm-admin-analytics-range">Date range</Form.Label>
          <Form.Select
            id="rwm-admin-analytics-range"
            value={analyticsRange}
            onChange={(e) => setAnalyticsRange(e.target.value)}
            data-testid="rwm-admin-analytics-range"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </Form.Select>
        </Form.Group>
        <Button
          variant="info"
          onClick={generateAnalytics}
          disabled={analyticsLoading}
          data-testid="rwm-admin-analytics-generate"
        >
          {analyticsLoading ? 'Generating…' : 'Generate Report'}
        </Button>
      </div>

      {analyticsError ? (
        <Alert variant="danger" data-testid="rwm-admin-analytics-error">
          {analyticsError}
        </Alert>
      ) : null}

      {analytics ? (
        <div className="rwm-admin__analytics-results" data-testid="rwm-admin-analytics-results">
          <p className="text-muted small mb-3">
            Report range: <strong>{analytics.range}</strong>
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-3 col-6">
              <div className="rwm-admin__stat-card">
                <span className="rwm-admin__stat-label">Companies</span>
                <span className="rwm-admin__stat-value">{analytics.total_companies}</span>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="rwm-admin__stat-card">
                <span className="rwm-admin__stat-label">Candidates</span>
                <span className="rwm-admin__stat-value">{analytics.total_candidates}</span>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="rwm-admin__stat-card">
                <span className="rwm-admin__stat-label">Applications</span>
                <span className="rwm-admin__stat-value">{analytics.total_applications}</span>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="rwm-admin__stat-card">
                <span className="rwm-admin__stat-label">Total jobs</span>
                <span className="rwm-admin__stat-value">{analytics.total_jobs}</span>
              </div>
            </div>
          </div>
          <h2 className="h6 mb-3">Jobs by status</h2>
          <Table size="sm" className="rwm-admin__table mb-0">
            <tbody>
              {Object.entries(analytics.jobs_by_status || {}).map(([status, count]) => (
                <tr key={status}>
                  <td className="text-capitalize">{status.replace('_', ' ')}</td>
                  <td className="text-end">{count}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <p className="text-muted mb-0" data-testid="rwm-admin-analytics-placeholder">
          Click Generate Report to load analytics.
        </p>
      )}
    </>
  );
}
